import React from "react";
import { Button } from "../../../components/ui/button";
import {
  AlertTriangle,
  Calendar,
  CheckCircle,
  ExternalLink,
  Eye,
  FileSpreadsheet,
  FileText,
  Info,
  Mail,
  MessageSquare,
  MoreHorizontal,
  Users,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../components/ui/popover";
import { Card } from "../../../components/ui/card";
import { Link } from "react-router-dom";
import { CSVLink } from "react-csv";
import moment from "moment";
import styles from "../Report.module.scss";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../components/ui/tooltip";
import { Badge } from "../../../components/ui/badge";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "../../../components/ui/hover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../../components/ui/dialog";

const CommonDownloadBar = ({
  group,
  handleEmailIconClick,
  downloadForPDF,
  downloadForExcel,
  clientsListCSV,
  reportName,
  startDate,
  endDate,
  setDownloadCSVData,
}) => (
  <div className="flex flex-center flex-space-between sm:flex-row sm:justify-between sm:items-center bg-gray-50 dark:bg-gray-800 p-12 rounded-lg gap-2">
    <h3 className="font-medium text-gray-900 dark:text-white text-sm">
      {group?.employeeName}
    </h3>
    <div className="flex items-center space-x-2 self-start sm:self-center">
      <Button
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0 hover:bg-green-50"
        onClick={() => handleEmailIconClick(group)}
      >
        <Mail className="h-3 w-3 text-green-600" />
      </Button>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-8">
            <MoreHorizontal className="mr-4 h-3 w-3" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-40">
          <div className="space-y-2">
            <Button
              variant="ghost"
              className="w-full justify-start h-8"
              onClick={() => downloadForPDF(group)}
            >
              <FileText className="mr-8 h-3 w-3" />
              PDF
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start h-8"
              onClick={() => downloadForExcel(group)}
            >
              <FileSpreadsheet className="mr-8 h-3 w-3" />
              Excel
            </Button>
            <CSVLink
              data={clientsListCSV}
              filename={`${reportName}${group?.employeeName}_${moment(startDate).format("YYYY-MM-DD")}_${moment(endDate).format("YYYY-MM-DD")}.csv`}
              asyncOnClick={true}
              onClick={(event, done) =>
                setDownloadCSVData(event, done, group)
              }
              className="w-full flex justify-start items-center h-8 pv-8 ph-16 text-sm hover:bg-accent hover:text-accent-foreground rounded-md transition-colors"
            >
              <FileSpreadsheet className="mr-16 h-4 w-4" />
              <span className="font-medium">CSV</span>
            </CSVLink>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  </div>
);

export function MobileShiftVarianceReport({
  MobileViewData,
  handleEmailIconClick,
  downloadForPDF,
  downloadForExcel,
  clientsListCSV,
  setDownloadCSVData,
  startDate,
  endDate,
  reportName,
}) {
  const getDeltaColor = (delta) => {
    if (delta === "0") return "text-[#05500e]";
    if (delta?.startsWith("-")) return "text-red-500";
    return "text-[#0b48ff]";
  };

  return MobileViewData?.map((group) => (
    <div key={group?.employeeName} className="space-y-3 mb-15">
      <CommonDownloadBar
        group={group}
        handleEmailIconClick={handleEmailIconClick}
        downloadForPDF={downloadForPDF}
        downloadForExcel={downloadForExcel}
        clientsListCSV={clientsListCSV}
        reportName={reportName}
        startDate={startDate}
        endDate={endDate}
        setDownloadCSVData={setDownloadCSVData}
      />

      {group?.records?.map((record) => (
        <Card key={record?.id} className="ml-2 shadow-sm">
          <div className="p-12 space-y-3">
            <div className="font-medium text-xs text-gray-900 dark:text-white pb-8 border-b">
              {record?.date}
            </div>
            <div className="space-y-3">
              <div className="mb-15">
                <div className="mb-8 d-flex flex-center flex-space-between">
                  <span className="text-gray-500 text-xs uppercase tracking-wide block mb-4">
                    Start Times
                  </span>
                  <div className={`${getDeltaColor(record?.startVariance)} font-medium`}>
                    {record?.startVariance}
                  </div>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <div>
                    <div className="text-gray-600">Scheduled</div>
                    <div className="text-gray-400">{record?.scheduledStart}</div>
                  </div>
                  <div>
                    <div className="text-gray-600 font-medium">Actual</div>
                    <div className="text-gray-600 font-medium">
                      {record?.actualStart !== "Not Available" ? (
                        <Link
                          className={styles.timeZone}
                          target="_blank"
                          rel="noopener noreferrer"
                          href={`https://maps.google.com/?q=${record?.location?.lat},${record?.location?.lng}`}
                        >
                          {record?.actualStart}
                        </Link>
                      ) : (
                        record?.actualStart
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="mb-8 d-flex flex-center flex-space-between">
                  <span className="text-gray-500 text-xs uppercase tracking-wide block mb-4">
                    End Times
                  </span>
                  <div className={`${getDeltaColor(record?.endVariance)} font-medium`}>
                    {record?.endVariance}
                  </div>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <div>
                    <div className="text-gray-600">Scheduled</div>
                    <div className="text-gray-400">{record?.scheduledEnd}</div>
                  </div>
                  <div>
                    <div className="text-gray-600 font-medium">Actual</div>
                    <div className="text-gray-600 font-medium">
                      {record?.actualEnd !== "Not Available" ? (
                        <Link
                          className={styles.timeZone}
                          target="_blank"
                          rel="noopener noreferrer"
                          href={`https://maps.google.com/?q=${record?.shiftend_location?.lat},${record?.shiftend_location?.lng}`}
                        >
                          {record?.actualEnd}
                        </Link>
                      ) : (
                        record?.actualEnd
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  ));
}

export function MobileCheckInCheckOutReport({
  MobileViewData,
  handleEmailIconClick,
  downloadForPDF,
  downloadForExcel,
  clientsListCSV,
  setDownloadCSVData,
  startDate,
  endDate,
  reportName,
}) {

  return MobileViewData?.map((group) => (
    <TooltipProvider>
    <div key={group?.employeeName} className="space-y-3 mb-15">
      <CommonDownloadBar
        group={group}
        handleEmailIconClick={handleEmailIconClick}
        downloadForPDF={downloadForPDF}
        downloadForExcel={downloadForExcel}
        clientsListCSV={clientsListCSV}
        reportName={reportName}
        startDate={startDate}
        endDate={endDate}
        setDownloadCSVData={setDownloadCSVData}
      />

      {group?.records?.map((record) => (
        <Card key={record?.id} className="ml-2 shadow-sm">
          <div className="p-12 space-y-3">
            <div className="font-medium text-xs text-gray-900 dark:text-white pb-8 border-b">
              {record?.date}
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center text-xs">
                <div>
                  <div className="text-gray-500 mb-4">Check In</div>
                  <div className="text-blue-600 font-medium">
                    {record?.checkInTime}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500 mb-4">Check Out</div>
                  <div className="text-blue-600 font-medium">
                    {record?.checkOutTime}
                  </div>
                </div>
              </div>
              <div className="flex justify-between items-center text-xs">
                <div>
                  <div className="text-gray-500 mb-4">Total Time</div>
                  <div className="text-gray-900 dark:text-white font-medium">
                    {record?.totalTime}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500 mb-4">Company</div>
                  <div className="text-gray-600 text-xs">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="cursor-default">
                          {record?.companyName?.length > 25
                            ? `${record?.companyName?.slice(0, 25)}...`
                            : record?.companyName}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>{record?.companyName}</TooltipContent>
                    </Tooltip>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      ))}
    </div>
    </TooltipProvider>
  ));
}

export function MobileActivityReport({
  MobileViewData,
  handleEmailIconClick,
  downloadForPDF,
  downloadForExcel,
  clientsListCSV,
  setDownloadCSVData,
  startDate,
  endDate,
  reportName,
}) {

  const getActionBadgeColor = (action) => {
    const actionType = action?.split(" ")[0]?.toUpperCase();

    switch (actionType) {
      case "CHECKIN":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
      case "CHECKOUT":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
      case "LOGIN":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200";
    }
  };

  return MobileViewData?.map((group) => (
    <div key={group?.employeeName} className="space-y-3 mb-15">
      <CommonDownloadBar
        group={group}
        handleEmailIconClick={handleEmailIconClick}
        downloadForPDF={downloadForPDF}
        downloadForExcel={downloadForExcel}
        clientsListCSV={clientsListCSV}
        reportName={reportName}
        startDate={startDate}
        endDate={endDate}
        setDownloadCSVData={setDownloadCSVData}
      />

      {group?.records?.map((record) => (
        <Card key={record?.id} className="shadow-sm">
          <div className="p-12 space-y-3">
            <div className="flex justify-between items-start border-b">
              <div className="font-medium dark:text-white mt-4">{record?.date}</div>
            </div>
            <div className="space-y-2 d-flex flex-center flex-space-between">
              <div>
                <div className="text-gray-500 text-xs mb-4">Device</div>
                <div className="text-xs text-gray-700 dark:text-gray-300">
                  {record?.version}
                </div>
              </div>
              <div>
                <div className="text-gray-500 text-xs mb-4">Action</div>
                <span
                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getActionBadgeColor(
                    record?.actions
                  )}`}
                >
                  {record?.actions}
                </span>
              </div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  ));
}
export function MobileUnReadMessage({
  MobileViewData,
  handleEmailIconClick,
  downloadForPDF,
  downloadForExcel,
  clientsListCSV,
  setDownloadCSVData,
  startDate,
  endDate,
  reportName,
}) {


  return MobileViewData?.map((group) => (
    <div key={group?.employeeName} className="space-y-3 mb-15">
      <CommonDownloadBar
        group={group}
        handleEmailIconClick={handleEmailIconClick}
        downloadForPDF={downloadForPDF}
        downloadForExcel={downloadForExcel}
        clientsListCSV={clientsListCSV}
        reportName={reportName}
        startDate={startDate}
        endDate={endDate}
        setDownloadCSVData={setDownloadCSVData}
      />

      {group?.records?.map((record) => (
        <Card key={record?.id} className="shadow-sm">
          <div className="p-12 space-y-3">
            <div className="flex justify-between items-center text-xs border-b pb-10">
              
              <Badge variant="outline"> {record?.type}</Badge>
            
              <Badge variant={parseFloat(record?.openRate) > 25 ? "destructive" : parseFloat(record?.openRate) > 15 ? "secondary" : "outline"} className="text-sm">
                {record?.rate}
              </Badge>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-muted/50 d-flex flex-center flex-direction-column flex-justify-center p-8 rounded">
                <p className="text-xs text-muted-foreground font-medium mb-5">
                  Received
                </p>
                <div className="text-medium  font-medium ">
                  {record?.received}
                </div>
              </div>
              <div className="bg-muted/50 d-flex flex-center flex-direction-column flex-justify-center p-8 rounded">
                <p className="text-xs text-muted-foreground font-medium mb-5">
                  UnRead
                </p>
                <div className="text-medium  text-red-400">
                  {record?.unread}
                </div>
              </div>

            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-muted/50 d-flex flex-center flex-direction-column flex-justify-center p-8 rounded">
                <p className="text-xs text-muted-foreground font-medium mb-5">
                  OldestDate
                </p>
                <div className="text-medium  font-medium">
                  {record?.oldestDate}
                </div>
              </div>
              <div className="bg-muted/50 d-flex flex-center flex-direction-column flex-justify-center p-8 rounded">
                <p className="text-xs text-muted-foreground font-medium mb-5">
                  AvgTime
                </p>
                <div className="text-medium  font-medium">
                  {record?.avgtime}
                </div>
              </div>

            </div>
          </div>
        </Card>
      ))}
    </div>
  ));
}

export function MobileMissedShiftReport({
  MobileViewData,
  handleEmailIconClick,
  downloadForPDF,
  downloadForExcel,
  clientsListCSV,
  setDownloadCSVData,
  startDate,
  endDate,
  reportName,
}) {
  const getShiftStateBadge = (state) => {
    const baseClasses =
      "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium min-w-[80px] justify-center";

    if (state === "Missed") {
      return (
        <span
          className={`${baseClasses} bg-red-100 text-red-800 dark:bg-red-900 dark:text-white`}
        >
          {state}
        </span>
      );
    }
    if (state === "No Check Out") {
      return (
        <span
          className={`${baseClasses} bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-white`}
        >
          {state}
        </span>
      );
    }
    if (state === "Complete") {
      return (
        <span
          className={`${baseClasses} bg-green-100 text-green-800 dark:bg-green-900 dark:text-white`}
        >
          {state}
        </span>
      );
    }
    return (
      <span
        className={`${baseClasses} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-white`}
      >
        {state}
      </span>
    );
  };

  return MobileViewData?.map((group) => (
    <div key={group?.employeeName} className="space-y-3 mb-15">
      <CommonDownloadBar
        group={group}
        handleEmailIconClick={handleEmailIconClick}
        downloadForPDF={downloadForPDF}
        downloadForExcel={downloadForExcel}
        clientsListCSV={clientsListCSV}
        reportName={reportName}
        startDate={startDate}
        endDate={endDate}
        setDownloadCSVData={setDownloadCSVData}
      />

      {group?.records?.map((record) => (
        <Card key={record?.id} className="shadow-sm">
          <div className="p-12 space-y-3">
            <div className="flex justify-between items-start pb-8 border-b">
              <div className="font-medium dark:text-white mt-4">
                {record?.date}
              </div>
            </div>
            <div className="space-y-3">
              <div className="d-flex flex-space-between flex-center">
                <div className="font-medium text-xs text-gray-900 dark:text-white">
                  {record?.shiftName}
                </div>
                <div>{getShiftStateBadge(record?.shiftState)}</div>
              </div>
              <div className="d-flex flex-space-between flex-center">
                <div className="text-gray-500 mb-4">Company</div>
                <div className="text-blue-600 font-medium">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-default">
                        {record?.company?.length > 25
                          ? `${record?.company?.slice(0, 25)}...`
                          : record?.company}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>{record?.company}</TooltipContent>
                  </Tooltip>
                </div>
              </div>
              <div className="flex justify-between text-xs">
                <div>
                  <div className="text-gray-500 mb-4">Start Time</div>
                  <div className="text-foreground font-medium">
                    {record?.startTime}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500 mb-4">End Time</div>
                  <div className="text-foreground font-medium">
                    {record?.endTime}
                  </div>
                </div>
              </div>
              <div className="flex justify-between text-xs">
                <div>
                  <div className="text-gray-500 mb-4">Punch In</div>
                  <div className="text-blue-600 font-medium">
                    {record?.checkInTime}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500 mb-4">Punch Out</div>
                  <div className="text-blue-600 font-medium">
                    {record?.checkOutTime}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  ));
}

export function MobileTasksPerEmployee({
  MobileViewData,
  handleEmailIconClick,
  downloadForPDF,
  downloadForExcel,
  clientsListCSV,
  setDownloadCSVData,
  startDate,
  endDate,
  reportName,
}) {

  const TaskTooltip = ({ tasks, type }) => (
    <div className="max-w-xs">
      <h4 className="font-medium text-sm mb-8 capitalize">{type} Tasks ({tasks?.length})</h4>
      <div className="space-y-1 max-h-48 overflow-y-auto">
        {tasks?.map((task) => (
          <div key={task.id} className="text-xs p-8 bg-white dark:bg-gray-700 rounded border">
            <div className="font-medium">{task?.title}</div>
            {/* <div className="text-gray-500">
              {type === 'assigned' ? 'Assigned' : 'Completed'}: {type === 'assigned' ? task.assignedDate : task.completedDate}
            </div> */}
          </div>
        ))}
      </div>
    </div>
  );

  return MobileViewData?.map((group) => (
    <TooltipProvider>
      <div key={group?.employeeName} className="space-y-3 mb-15">
        <CommonDownloadBar
          group={group}
          handleEmailIconClick={handleEmailIconClick}
          downloadForPDF={downloadForPDF}
          downloadForExcel={downloadForExcel}
          clientsListCSV={clientsListCSV}
          reportName={reportName}
          startDate={startDate}
          endDate={endDate}
          setDownloadCSVData={setDownloadCSVData}
        />

        {group?.records?.map((record) => (
          <Card key={record?.id} className="shadow-sm">
            <div className="p-12 space-y-3">
              <div className="flex justify-between items-start pb-8 border-b">
                <div className="font-medium dark:text-white mt-4">
                  {record?.date}
                </div>
              </div>
              <div className="space-y-3">
                <div className="d-flex flex-space-between flex-center">
                  <div className="font-medium text-xs text-gray-900 dark:text-white">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="cursor-default">
                          {record?.client?.length > 25
                            ? `${record?.client?.slice(0, 25)}...`
                            : record?.client}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>{record?.client}</TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="flex justify-center">
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${parseFloat(record?.comparition) >= 80
                          ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                          : parseFloat(record?.comparition) >= 60
                            ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"
                            : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                        }`}
                    >
                      {parseFloat(record?.comparition) >= 80
                        ? "Excellent"
                        : parseFloat(record?.comparition) >= 60
                          ? "Good"
                          : "Needs Attention"}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4 mb-16">
                  <div className="text-center">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="cursor-help">
                          <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                            {record?.assigned}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            Assigned
                          </div>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs">
                        <TaskTooltip
                          tasks={record?.assignedTasks}
                          type="assigned"
                        />
                      </TooltipContent>
                    </Tooltip>
                  </div>

                  <div className="text-center">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="cursor-help">
                          <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                            {record?.completed}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            Completed
                          </div>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs">
                        <TaskTooltip
                          tasks={record?.completedTasks}
                          type="completed"
                        />
                      </TooltipContent>
                    </Tooltip>
                  </div>

                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                      {record?.comparition}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Completion
                    </div>
                  </div>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-8">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${parseFloat(record?.comparition) >= 80
                        ? "bg-green-500"
                        : parseFloat(record?.comparition) >= 60
                          ? "bg-yellow-500"
                          : "bg-red-500"
                      }`}
                    style={{ width: record?.comparition }}
                  ></div>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </TooltipProvider>
  ));
}

export function MobileIssueResolutionReport({
  MobileViewData,
  handleEmailIconClick,
  downloadForPDF,
  downloadForExcel,
  clientsListCSV,
  setDownloadCSVData,
  startDate,
  endDate,
  reportName,
}) {

  const getDaysOpenColor = (days) => {
    if (days <= 1) return 'text-green-600';
    if (days <= 3) return 'text-yellow-600';
    return 'text-red-600';
  };

  return MobileViewData?.map((group) => (
    <TooltipProvider>
      <div key={group?.employeeName} className="space-y-3 mb-15">
        <CommonDownloadBar
          group={group}
          handleEmailIconClick={handleEmailIconClick}
          downloadForPDF={downloadForPDF}
          downloadForExcel={downloadForExcel}
          clientsListCSV={clientsListCSV}
          reportName={reportName}
          startDate={startDate}
          endDate={endDate}
          setDownloadCSVData={setDownloadCSVData}
        />

        {group?.records?.map((record) => (
          <Card key={record?.id} className="shadow-sm">
            <div className="p-12 space-y-3">
              <div className="flex justify-between items-start pb-8 border-b">
                <div className="font-medium dark:text-white mt-4">
                  {record?.date}
                </div>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${record?.status === "Pending"
                      ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                      : record?.status === "Closed"
                        ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                        : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                    }`}
                >
                  {record?.status}
                </span>
              </div>
              <div className="space-y-3">
                <div className="d-flex flex-space-between flex-center">
                  <Link
                    to={
                      record?.archive === true
                        ? `/operations/issues/archive/${record?.id}`
                        : `/operations/issues/manage/${record?.id}`
                    }
                    className="text-blue-600 font-medium hover:text-blue-800 flex items-center gap-1"
                  >
                    {record?.issues}
                    <ExternalLink className="w-3 h-3" />
                  </Link>
                  <div className="d-flex flex-center">
                    <div className="truncate text-blue-600 max-w-[150px] font-medium mr-5">
                      {record?.details}
                    </div>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <div className="space-y-1">
                          <p>
                            <strong>Company:</strong> {record?.company}
                          </p>
                          <p>
                            <strong>Opened:</strong> {record?.open}
                          </p>
                          {record?.close && (
                            <p>
                              <strong>Closed:</strong> {record?.close}
                            </p>
                          )}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-12 mb-16">
                  <div className="text-center">
                    <div
                      className={`text-2xl font-bold ${getDaysOpenColor(
                        record?.daysOpen
                      )}`}
                    >
                      {record?.daysOpen}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide font-medium">
                      Days Open
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </TooltipProvider>
  ));
}

export function MobileIssueEscalationReport({
  MobileViewData,
  handleEmailIconClick,
  downloadForPDF,
  downloadForExcel,
  clientsListCSV,
  setDownloadCSVData,
  startDate,
  endDate,
  reportName,
}) {

  const getEscalationSpeedColor = (days) => {
    if (days === 0) return 'text-red-600';
    if (days <= 1) return 'text-orange-600';
    if (days <= 3) return 'text-yellow-600';
    return 'text-green-600';
  };

  return MobileViewData?.map((group) => (
    <div key={group?.employeeName} className="space-y-3 mb-15">
      <CommonDownloadBar
        group={group}
        handleEmailIconClick={handleEmailIconClick}
        downloadForPDF={downloadForPDF}
        downloadForExcel={downloadForExcel}
        clientsListCSV={clientsListCSV}
        reportName={reportName}
        startDate={startDate}
        endDate={endDate}
        setDownloadCSVData={setDownloadCSVData}
      />

      {group?.records?.map((record) => (
        <Card key={record?.id} className="shadow-sm">
          <div className="p-12 space-y-3">
            <div className="flex justify-between items-start pb-8 border-b">
              <div className="font-medium dark:text-white mt-4">
                {record?.date}
              </div>
              <span
                className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${record?.status === "Pending"
                    ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                    : record?.status === "Closed"
                      ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                      : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                  }`}
              >
                {record?.status}
              </span>
            </div>
            <div className="space-y-3">
              <div className="d-flex flex-space-between flex-center">
                <Link
                  to={
                    record?.archive === true
                      ? `/operations/issues/archive/${record?.id}`
                      : `/operations/issues/manage/${record?.id}`
                  }
                  className="text-blue-600 font-medium hover:text-blue-800 flex items-center gap-1"
                >
                  {record?.issues}
                  <ExternalLink className="w-3 h-3" />
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-16">
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-12 text-center">
                  <div className="text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wide mb-4">
                    opened
                  </div>
                  <div className="text-sm font-semibold text-gray-900 dark:text-white">
                    {record?.opened}
                  </div>
                </div>

                <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-12 text-center">
                  <div className="text-xs font-medium text-orange-600 dark:text-orange-400 uppercase tracking-wide mb-4">
                    Escalated
                  </div>
                  <div className="text-sm font-semibold text-gray-900 dark:text-white">
                    {record?.escalated}
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-16 mb-16 text-center">
                <div className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-4">  Days to Escalation </div>
                <div className={`text-2xl font-bold ${getEscalationSpeedColor(record?.daystoescalation)}`}> {record?.daystoescalation} </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-4">
                  {record?.daystoescalation === 0 ? "Same day" : record?.daystoescalation === 1 ? "1 day" : `${record?.daystoescalation} days`}
                </div>
              </div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  ));
}

export function MobileEmpSupplyRequestReport({
  MobileViewData,
  handleEmailIconClick,
  downloadForPDF,
  downloadForExcel,
  clientsListCSV,
  setDownloadCSVData,
  startDate,
  endDate,
  reportName,
}) {

  const renderItemsList = (items) => {
    // if (items?.length === 1) {
    //   return `${items[0]?.title} (${items[0]?.qty})`;
    // }

    return (
      <HoverCard>
        <HoverCardTrigger asChild>
          <button className="text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1">
            <span> {items?.length} items </span>
            <Eye className="h-3 w-3" />
          </button>
        </HoverCardTrigger>
        <HoverCardContent className="w-80 pt-4 ph-20 pb-10">
          <div className="space-y-2">
            <h4 className="text-sm font-semibold">Requested Items:</h4>
            <div className="space-y-1">
              {items?.map((item, index) => (
                <div key={index} className="text-sm flex justify-between">
                  <span>{item?.title}</span>
                  <span className="text-gray-500">({item?.qty})</span>
                </div>
              ))}
            </div>
          </div>
        </HoverCardContent>
      </HoverCard>
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Delivered':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'Ordered':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
      case 'Pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'Rejected':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  return MobileViewData?.map((group) => (
    <TooltipProvider>
      <div key={group?.employeeName} className="space-y-3 mb-15">
        <CommonDownloadBar
          group={group}
          handleEmailIconClick={handleEmailIconClick}
          downloadForPDF={downloadForPDF}
          downloadForExcel={downloadForExcel}
          clientsListCSV={clientsListCSV}
          reportName={reportName}
          startDate={startDate}
          endDate={endDate}
          setDownloadCSVData={setDownloadCSVData}
        />

        {group?.records?.map((record) => (
          <Card key={record?.id} className="shadow-sm">
            <div className="p-12 space-y-3">
              <div className="flex justify-between items-start pb-8 border-b">
                <div className="font-medium dark:text-white mt-4">
                  {record?.date}
                </div>
                <Badge className={getStatusColor(record?.status)}>
                  {record?.status}
                </Badge>
              </div>
              <div className="space-y-3">
                <div className="d-flex flex-space-between flex-center">
                  <div className="font-medium text-xs text-gray-900 dark:text-white">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="cursor-default">
                          {record?.client?.length > 25
                            ? `${record?.client?.slice(0, 25)}...`
                            : record?.client}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>{record?.client}</TooltipContent>
                    </Tooltip>
                  </div>
                  <div>
                    {record?.comments ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            aria-label="Info with comment"
                          >
                            <Info className="h-3 w-3" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p className="text-sm">{record.comments}</p>
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        aria-label="Info"
                      >
                        <Info className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </div>
                <div className="flex justify-between text-xs">
                  <div>
                    <div className="text-gray-500 mb-4">Items</div>
                    <div className="text-foreground font-medium">
                      {renderItemsList(record?.supplyItems)}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500 mb-4">QTY</div>
                    <div className="text-foreground font-medium">
                      {record?.qty}
                    </div>
                  </div>
                </div>
                <div className="flex justify-between text-xs">
                  <div>
                    <div className="text-gray-500 mb-4">Filled By</div>
                    <div className="font-medium">{record?.filledby}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 mb-4">Time</div>
                    <div className="font-medium">{record?.time}</div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </TooltipProvider>
  ));
}

export function MobileUrgentSupplyReport({
  MobileViewData,
  handleEmailIconClick,
  downloadForPDF,
  downloadForExcel,
  clientsListCSV,
  setDownloadCSVData,
  startDate,
  endDate,
  reportName,
}) {

  const renderItemsList = (items) => {
    // if (items?.length === 1) {
    //   return `${items[0]?.title} (${items[0]?.qty})`;
    // }

    return (
      <HoverCard>
        <HoverCardTrigger asChild>
          <button className="text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1">
            <span> {items?.length} items </span>
            <Eye className="h-3 w-3" />
          </button>
        </HoverCardTrigger>
        <HoverCardContent className="w-80 pt-4 ph-20 pb-10">
          <div className="space-y-2">
            <h4 className="text-sm font-semibold">Requested Items:</h4>
            <div className="space-y-1">
              {items?.map((item, index) => (
                <div key={index} className="text-sm flex justify-between">
                  <span>{item?.title}</span>
                  <span className="text-gray-500">({item?.qty})</span>
                </div>
              ))}
            </div>
          </div>
        </HoverCardContent>
      </HoverCard>
    );
  };

  const getStatusColor = (urgent) => {
    switch (urgent) {
      case 'No':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
      case 'Yes':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      default:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
    }
  };

  return MobileViewData?.map((group) => (
    <TooltipProvider>
      <div key={group?.employeeName} className="space-y-3 mb-15">
        <CommonDownloadBar
          group={group}
          handleEmailIconClick={handleEmailIconClick}
          downloadForPDF={downloadForPDF}
          downloadForExcel={downloadForExcel}
          clientsListCSV={clientsListCSV}
          reportName={reportName}
          startDate={startDate}
          endDate={endDate}
          setDownloadCSVData={setDownloadCSVData}
        />

        {group?.records?.map((record) => (
          <Card key={record?.id} className="shadow-sm">
            <div className="p-12 space-y-3">
              <div className="flex justify-between items-start pb-8 border-b">
                <div className="font-medium dark:text-white mt-4">
                  {record?.date}
                </div>
                <Badge className={getStatusColor(record?.urgent)}>
                  {record?.urgent}
                </Badge>
              </div>
              <div className="space-y-3">
                <div className="d-flex flex-space-between flex-center">
                  <div className="font-medium text-xs text-gray-900 dark:text-white">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="cursor-default">
                          {record?.client?.length > 25
                            ? `${record?.client?.slice(0, 25)}...`
                            : record?.client}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>{record?.client}</TooltipContent>
                    </Tooltip>
                  </div>
                </div>
                <div className="flex justify-between text-xs">
                  <div>
                    <div className="text-gray-500 mb-4">Items</div>
                    <div className="text-foreground font-medium">
                      {renderItemsList(record?.supplyItems)}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500 mb-4">QTY</div>
                    <div className="text-foreground font-medium">
                      {record?.qty}
                    </div>
                  </div>
                </div>
                <div className="flex justify-between text-xs">
                  <div>
                    <div className="text-gray-500 mb-4">Approved (hrs)</div>
                    <div className="font-medium">{record?.approved}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 mb-4">
                      Fulfilled Time (hrs)
                    </div>
                    <div className="font-medium">{record?.time}</div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </TooltipProvider>
  ));
}

export function MobileMessageSentEmpReport({
  MobileViewData,
  handleEmailIconClick,
  downloadForPDF,
  downloadForExcel,
  clientsListCSV,
  setDownloadCSVData,
  startDate,
  endDate,
  reportName,
}) {
  return MobileViewData?.map((group) => (
    <TooltipProvider>
      <div key={group?.employeeName} className="space-y-3 mb-15">
        <CommonDownloadBar
          group={group}
          handleEmailIconClick={handleEmailIconClick}
          downloadForPDF={downloadForPDF}
          downloadForExcel={downloadForExcel}
          clientsListCSV={clientsListCSV}
          reportName={reportName}
          startDate={startDate}
          endDate={endDate}
          setDownloadCSVData={setDownloadCSVData}
        />
        {group?.records?.map((record) => (
          <Card key={record?.id} className="shadow-sm">
            <div className="p-12 space-y-3">
              <div className="flex justify-between items-start pb-8 border-b">
                <div className="font-medium dark:text-white mt-4">
                  {record?.date} at {record?.time}
                </div>
                {record?.type === "Multiple" ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex items-center gap-1 cursor-pointer">
                        <Users className="h-3 w-3" />
                        Multiple
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <div className="max-w-48">
                        <p className="font-semibold mb-4">Recipients:</p>
                        <ul className="text-sm space-y-1">
                          {record?.Employee?.map((recipient, index) => (
                            <li key={index}>• {recipient?.name}</li>
                          ))}
                        </ul>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <span>Single</span>
                )}
              </div>
              <div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Channel</span>
                  <div>
                    {record?.channel?.split(",")?.map((ch, idx) => (
                      <Badge key={idx} variant="outline" className="mh-3">
                        {ch?.trim()}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="d-flex flex-space-between flex-center">
                  <div className="font-medium text-xs text-gray-900 dark:text-white">
                    {record?.client}
                  </div>
                </div>
                <div className="space-y-2">
                  <span className="text-sm text-muted-foreground">
                    Message Details:
                  </span>
                  <p className="text-sm cursor-pointer underline decoration-dotted line-clamp-2">
                    {record?.details}
                  </p>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </TooltipProvider>
  ));
}

export function MobileEmployeePerformanceReport({
  MobileViewData,
  handleEmailIconClick,
  downloadForPDF,
  downloadForExcel,
  clientsListCSV,
  setDownloadCSVData,
  startDate,
  endDate,
  reportName,
}) {

  const getPerformanceColor = (rate) => {
    const numericRate = typeof rate === "string" && rate.includes('%')
      ? parseFloat(rate.replace('%', ''))
      : Number(rate);
    if (numericRate >= 95) return "text-green-600";
    if (numericRate >= 85) return "text-yellow-600";
    return "text-red-600";
  };

  return MobileViewData?.map((group) => (
    <TooltipProvider>
      <div key={group?.employeeName} className="space-y-3 mb-15">
        <CommonDownloadBar
          group={group}
          handleEmailIconClick={handleEmailIconClick}
          downloadForPDF={downloadForPDF}
          downloadForExcel={downloadForExcel}
          clientsListCSV={clientsListCSV}
          reportName={reportName}
          startDate={startDate}
          endDate={endDate}
          setDownloadCSVData={setDownloadCSVData}
        />
        {group?.records?.map((record) => (
          <Card key={record?.id} className="shadow-sm">
            <div className="p-12 space-y-3">
              <div className="d-flex flex-space-between flex-center">
                <span className="font-medium text-foreground">
                  Details
                </span>
                <div className="flex gap-1">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm">
                        <Eye className="h-4 w-4" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-sm mx-4">
                      <DialogHeader>
                        <DialogTitle className="text-lg text-foreground">
                          {record?.employeeName} - All Details
                        </DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 mt-16">
                        <div className="space-y-3 text-sm">
                          <div className="space-y-2">
                            <h5 className="font-medium text-primary dark:text-green-500">
                              Attendance
                            </h5>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <span className="text-muted-foreground">
                                  Rate:
                                </span>
                                <div
                                  className={`font-medium ${getPerformanceColor(
                                    record?.attendance
                                  )}`}
                                >
                                  {record?.attendance}
                                </div>
                              </div>
                              <div>
                                <span className="text-muted-foreground">
                                  Shifts:
                                </span>
                                <div className="font-medium text-foreground">
                                  {record?.shifts}
                                </div>
                              </div>
                              <div className="col-span-2">
                                <span className="text-muted-foreground">
                                  Clock Accuracy:
                                </span>
                                <div className="font-medium text-foreground">
                                  {record?.clockaccuracy}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="space-y-2">
                            <h5 className="font-medium text-primary dark:text-green-500">Tasks</h5>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <span className="text-muted-foreground">
                                  Rate:
                                </span>
                                <div
                                  className={`font-medium ${getPerformanceColor(
                                    record?.taskrate
                                  )}`}
                                >
                                  {record?.taskrate}
                                </div>
                              </div>
                              <div>
                                <span className="text-muted-foreground">
                                  Completed:
                                </span>
                                <div className="font-medium text-foreground">{record?.tasks}</div>
                              </div>
                            </div>
                          </div>

                          <div className="space-y-2">
                            <h5 className="font-medium text-primary dark:text-green-500">Issues</h5>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <span className="text-muted-foreground">
                                  Resolved:
                                </span>
                                <div className="font-medium text-foreground">
                                  {record?.issues}
                                </div>
                              </div>
                              <div>
                                <span className="text-muted-foreground">
                                  Avg Time:
                                </span>
                                <div className="font-medium text-foreground">
                                  {record?.avgresolution}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="space-y-2">
                            <h5 className="font-medium text-primary dark:text-green-500">
                              Communication
                            </h5>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <span className="text-muted-foreground">
                                  Engagement:
                                </span>
                                <div className={`font-medium text-foreground`}>
                                  {record?.supplyrequests}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2 p-12 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-1">
                    <Calendar className="h-4 w-4 text-primary dark:text-green-500" />
                    <span className="font-medium text-sm">Attendance</span>
                  </div>
                  <div className="text-sm">
                    <div
                      className={`font-medium ${getPerformanceColor(
                        record?.attendance
                      )}`}
                    >
                      {record?.attendance}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {record?.shifts} shifts
                    </div>
                  </div>
                </div>

                <div className="space-y-2 p-12 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-1">
                    <CheckCircle className="h-4 w-4 text-primary dark:text-green-500" />
                    <span className="font-medium text-sm">Tasks</span>
                  </div>
                  <div className="text-sm">
                    <div
                      className={`font-medium ${getPerformanceColor(
                        record?.taskrate
                      )}`}
                    >
                      {record?.taskrate}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {record?.tasks} tasks
                    </div>
                  </div>
                </div>

                <div className="space-y-2 p-12 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-1">
                    <AlertTriangle className="h-4 w-4 text-primary dark:text-green-500" />
                    <span className="font-medium text-sm">Issues</span>
                  </div>
                  <div className="text-sm">
                    <div className="font-medium">{record?.issues}</div>
                    <div className="text-xs text-muted-foreground">
                      {record?.avgresolution}
                    </div>
                  </div>
                </div>

                <div className="space-y-2 p-12 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-1">
                    <MessageSquare className="h-4 w-4 text-primary dark:text-green-500" />
                    <span className="font-medium text-sm">Engagement</span>
                  </div>
                  <div className="text-sm">
                    <div className={`font-medium`}>
                      {record?.supplyrequests}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </TooltipProvider>
  ));
}

